import { motion } from "framer-motion";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Check, X, Globe } from "@phosphor-icons/react";
import { ScoreGauge } from "@/components/ScoreGauge";
import { PROVIDER_META, INTENT_LABELS, scoreColor } from "@/lib/api";

const Section = ({ children, delay = 0 }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="print-break"
  >
    {children}
  </motion.section>
);

const Label = ({ children }) => (
  <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">{children}</p>
);

export default function ReportContent({ report }) {
  const s = report.summary_json;
  if (!s) return null;
  const providers = Object.keys(s.providers);

  return (
    <div className="space-y-12 md:space-y-16">
      {/* 1. Hero score */}
      <Section>
        <div className="border border-border p-6 md:p-10 grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center">
          <ScoreGauge score={s.overall_score} size={220} />
          <div>
            <Label>AI Marka Radar Skoru</Label>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-1">
              {report.brand_name}
            </h2>
            {(report.sector || report.city) && (
              <p className="font-mono text-xs text-muted-foreground mb-3">
                {[report.sector, report.city].filter(Boolean).join(" · ")}
              </p>
            )}
            <p className="text-muted-foreground text-base leading-relaxed max-w-xl">
              Skor, yapay zekâ asistanlarının markanızı ne sıklıkla ve ne kadar üst sırada
              önerdiğini gösterir. 100'e ne kadar yakınsanız, potansiyel müşteriler yapay zekâya
              sorduğunda o kadar görünürsünüz.
            </p>
            <p className="font-mono text-xs text-muted-foreground mt-5 border-l-2 border-border pl-3">
              {s.note}
            </p>
          </div>
        </div>
      </Section>

      {/* 2. Per-provider mention rate */}
      <Section delay={0.05}>
        <Label>Hangi AI'larda çıkıyorsun?</Label>
        <div className="grid sm:grid-cols-3 gap-4">
          {providers.map((p) => {
            const meta = PROVIDER_META[p];
            const stat = s.providers[p];
            const available = stat.available;
            const rate = Math.round((stat.mention_rate || 0) * 100);
            return (
              <div key={p} className="border border-border border-t-4 p-6" style={{ borderTopColor: meta.hex }} data-testid={`provider-card-${p}`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading font-semibold text-lg">{meta.label}</span>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: available ? meta.hex : "#D4D4D8" }} />
                </div>
                {available ? (
                  <>
                    <p className="font-heading font-black text-5xl tracking-tighter mt-4 tabular-nums">{rate}%</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {stat.total_runs} yanıttan {stat.mentions} tanesinde anıldı
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-heading font-black text-2xl tracking-tighter mt-4 text-muted-foreground">Kullanılamıyor</p>
                    <p className="text-sm text-muted-foreground mt-1">Bu sağlayıcı bu raporda çalıştırılamadı.</p>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {s.unavailable_providers && s.unavailable_providers.length > 0 && (
          <p className="font-mono text-xs text-muted-foreground mt-4 border-l-2 border-border pl-3">
            Not: {s.unavailable_providers.join(", ")} bu raporda yapılandırılmadığı için ölçülemedi ve skora dahil edilmedi.
          </p>
        )}
      </Section>

      {/* 3. Competitor comparison table */}
      <Section delay={0.1}>
        <Label>Rakip Karşılaştırma Tablosu</Label>
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="competitor-table">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="p-4 font-mono text-xs tracking-wider uppercase text-muted-foreground">Marka</th>
                <th className="p-4 font-mono text-xs tracking-wider uppercase text-muted-foreground text-right">Anılma Oranı</th>
                <th className="p-4 font-mono text-xs tracking-wider uppercase text-muted-foreground text-right">Ort. Sıra</th>
                <th className="p-4 font-mono text-xs tracking-wider uppercase text-muted-foreground text-right">Ses Payı</th>
              </tr>
            </thead>
            <tbody>
              {s.competitor_table.map((row, i) => (
                <tr key={i} className={`border-b border-border last:border-0 ${row.is_brand ? "bg-brand/5" : i % 2 ? "bg-muted/40" : ""}`}>
                  <td className="p-4 font-medium">
                    {row.is_brand && <span className="font-mono text-[10px] bg-brand text-white px-1.5 py-0.5 mr-2 align-middle">SEN</span>}
                    {row.name}
                  </td>
                  <td className="p-4 text-right tabular-nums">{Math.round(row.mention_rate * 100)}%</td>
                  <td className="p-4 text-right tabular-nums">{row.avg_rank ?? "—"}</td>
                  <td className="p-4 text-right tabular-nums font-medium">{Math.round(row.share_of_voice * 100)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 4. Per-question detail */}
      <Section delay={0.15}>
        <Label>Soru Bazında Detay</Label>
        <Accordion type="single" collapsible className="border border-border divide-y divide-border" data-testid="question-accordion">
          {s.questions.map((q) => (
            <AccordionItem key={q.id} value={q.id} className="border-0">
              <AccordionTrigger className="px-4 hover:no-underline text-left">
                <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-3 pr-3">
                  <span className="flex-1 font-medium">{q.question_text}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground border border-border px-1.5 py-0.5">
                      {INTENT_LABELS[q.intent_type] || q.intent_type} · {q.language}
                    </span>
                    {providers.map((p) => {
                      const pd = q.providers[p] || {};
                      const unavailable = pd.available === false;
                      const m = pd.mentioned;
                      return (
                        <span key={p} title={PROVIDER_META[p].label}
                          className="flex items-center gap-0.5 font-mono text-[10px]"
                          style={{ color: unavailable ? "#D4D4D8" : m ? PROVIDER_META[p].hex : "#A1A1AA" }}>
                          {unavailable ? "–" : m ? <Check size={13} weight="bold" /> : <X size={13} weight="bold" />}
                          {PROVIDER_META[p].label.slice(0, 4)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="font-mono text-xs text-muted-foreground leading-relaxed bg-muted/50 p-4 border-l-2 border-border">
                  {q.snippet || "Yanıt alınamadı."}
                  {q.snippet && "…"}
                </p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* 5. Cited sources */}
      <Section delay={0.2}>
        <Label>AI hangi kaynaklara güveniyor?</Label>
        {s.sources.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-border p-6">Henüz kaynak tespit edilmedi.</p>
        ) : (
          <div className="border border-border divide-y divide-border" data-testid="sources-list">
            {s.sources.map((src, i) => {
              const max = s.sources[0].count || 1;
              return (
                <div key={i} className="flex items-center gap-4 p-4">
                  <span className="font-mono text-xs text-muted-foreground w-6 tabular-nums">{i + 1}</span>
                  <Globe size={16} className="text-muted-foreground shrink-0" />
                  <a href={`https://${src.domain}`} target="_blank" rel="noreferrer"
                     className="font-medium text-sm hover:text-brand truncate w-48 sm:w-64">{src.domain}</a>
                  <div className="flex-1 h-2 bg-muted relative">
                    <div className="absolute inset-y-0 left-0 bg-brand" style={{ width: `${(src.count / max) * 100}%` }} />
                  </div>
                  <span className="font-mono text-xs tabular-nums w-10 text-right">{src.count}×</span>
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
